import React, { useState, useContext, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { useLocation } from "react-router-dom";
import Select from "react-select";

// 진동 애니메이션
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// 폼 섹션 애니메이션 variants 추가
const formVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const formContainerVariants = {
  initial: {
    height: "auto",
    opacity: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 },
    },
  },
  exit: {
    height: "auto",
    opacity: 0,
  },
};

const formContentVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 500 : -500,
    opacity: 0,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  }),
};

// 아이템 애니메이션 추가
const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.2 },
  },
};

// 리스트 아이템 애니메이션 (더 확실한 애니메이션 효과)
const listItemVariants = {
  initial: {
    opacity: 0,
    x: 50,
    scale: 0.8,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    x: -50,
    scale: 0.8,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

// 폼 컨테이너 애니메이션
const formContainerAnimation = {
  initial: {
    opacity: 0,
    height: 0,
    overflow: "hidden",
  },
  animate: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.5,
      ease: "easeInOut",
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.4,
      ease: "easeInOut",
    },
  },
};

// 모달 관련 스타일 컴포넌트 추가
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #00285b;
`;

const ModalContent = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 24px;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button`
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ModalCancelButton = styled(ModalButton)`
  background-color: #f1f1f1;
  color: #666;
`;

const ModalConfirmButton = styled(ModalButton)`
  background-color: #0075ff;
  color: white;
`;

// 은행 목록 데이터
const bankOptions = [
  { value: "KB", label: "KB국민은행", icon: "/bank-icons/kb.png" },
  { value: "shinhan", label: "신한은행", icon: "/bank-icons/shinhan.png" },
  { value: "woori", label: "우리은행", icon: "/bank-icons/woori.png" },
  { value: "smk", label: "새마을금고", icon: "/bank-icons/smk.png" },
  { value: "hana", label: "하나은행", icon: "/bank-icons/hana.png" },
  { value: "nh", label: "농협은행", icon: "/bank-icons/nh.png" },
  { value: "sh", label: "신협은행", icon: "/bank-icons/sh.png" },
  { value: "ibk", label: "기업은행", icon: "/bank-icons/ibk.png" },
  { value: "sc", label: "SC제일은행", icon: "/bank-icons/sc.png" },
  { value: "kakao", label: "카카오뱅크", icon: "/bank-icons/kakao.png" },
  { value: "toss", label: "토스뱅크", icon: "/bank-icons/toss.png" },
  { value: "kdb", label: "산업은행", icon: "/bank-icons/kdb.png" },
  { value: "city", label: "한국씨티은행", icon: "/bank-icons/city.png" },
  { value: "dgb", label: "대구은행", icon: "/bank-icons/dgb.png" },
  { value: "bnk", label: "부산은행", icon: "/bank-icons/bnk.png" },
  { value: "kjb", label: "광주은행", icon: "/bank-icons/kjb.png" },
  { value: "knb", label: "경남은행", icon: "/bank-icons/bnk.png" },
  { value: "kbank", label: "케이뱅크", icon: "/bank-icons/kbank.png" },
  { value: "suhyup", label: "Sh수협은행", icon: "/bank-icons/suhyup.png" },
  { value: "jeju", label: "제주은행", icon: "/bank-icons/jeju.png" },
  { value: "post", label: "우체국", icon: "/bank-icons/post.png" },
];

// 커스텀 옵션 컴포넌트
const CustomOption = ({ innerProps, label, data }) => (
  <OptionContainer {...innerProps}>
    <BankIcon src={data.icon} alt={label} />
    {label}
  </OptionContainer>
);

// 커스텀 단일 값 컴포넌트
const CustomSingleValue = ({ children, data }) => (
  <SingleValueContainer>
    <BankIcon src={data.icon} alt={children} />
    {children}
  </SingleValueContainer>
);

const CoOwnerDataCollection = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState(null);

  // 공동 소유자 상태 관리
  const [coOwners, setCoOwners] = useState([
    {
      id: 1,
      name: "",
      birth: "",
      phone: "",
      email: "",
      address: "",
      detailAddress: "",
      bank: null,
      accountNumber: "",
      accountHolder: "",
    },
  ]);
  const [currentCoOwnerIndex, setCurrentCoOwnerIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(Array(10).fill(false));

  const [form, setForm] = useState({
    name: "",
    birth: "",
    phone: "",
    email: "",
    address: "",
    detailAddress: "",
    bank: null,
    accountNumber: "",
    accountHolder: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [errors, setErrors] = useState({
    name: "",
    birth: "",
    phone: "",
    email: "",
    bank: "",
    accountNumber: "",
    accountHolder: "",
  });

  const [shakingFields, setShakingFields] = useState({
    name: false,
    birth: false,
    phone: false,
    email: false,
  });

  const nameRef = useRef(null);
  const birthRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);
  const addressRef = useRef(null);
  const detailAddressRef = useRef(null);
  const accountNumberRef = useRef(null);
  const accountHolderRef = useRef(null);
  const bankSelectRef = useRef(null);

  // 상태 추가
  const [editMode, setEditMode] = useState(Array(10).fill(true)); // 기본적으로 모든 폼은 편집 모드
  const [sameAddressStates, setSameAddressStates] = useState(
    Array(10).fill(false)
  );

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 외국인 알림을 위한 별도의 토스트 상태
  const [showToast2, setShowToast2] = useState(false);
  const [toastMessage2, setToastMessage2] = useState("");

  // 대표 신청자 번호 모달 관련 상태 추가
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isRepresentativePhone, setIsRepresentativePhone] = useState(false);

  // 외국인 알림 토스트 메시지 표시 함수
  const showForeignerToast = () => {
    setToastMessage2("외국인은 접수를 받지 않습니다");
    setShowToast2(true);
    setTimeout(() => {
      setShowToast2(false);
    }, 3000);
  };

  // 영어 문자 확인 함수
  const containsEnglish = (text) => {
    return /[a-zA-Z]/.test(text);
  };

  // 대표 신청자 주소 저장
  const [representativeAddress, setRepresentativeAddress] = useState(null);

  // 대표 신청자 주소 가져오기
  useEffect(() => {
    try {
      const savedAddress = Cookies.get("representativeAddress");
      if (savedAddress) {
        const addressData = JSON.parse(savedAddress);
        setRepresentativeAddress(addressData);
      }
    } catch (error) {
      console.error("대표 신청자 주소 불러오기 실패:", error);
    }
  }, []);

  // 카카오 주소 API 스크립트 로드
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // URL 파라미터에서 redirect 값 추출
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath(`/${redirect}`);
    }
  }, [location]);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, []);

  // 현재 공동 소유자 정보가 변경될 때마다 폼 업데이트
  useEffect(() => {
    if (coOwners[currentCoOwnerIndex]) {
      setForm(coOwners[currentCoOwnerIndex]);
      // 체크박스 상태 복원 (지정된 상태 사용)
      setUseSameAddress(sameAddressStates[currentCoOwnerIndex]);
    } else {
      setForm({
        name: "",
        birth: "",
        phone: "",
        email: "",
        address: "",
        detailAddress: "",
        bank: null,
        accountNumber: "",
        accountHolder: "",
      });
    }
  }, [currentCoOwnerIndex, coOwners, sameAddressStates]);

  // 모든 가능한 스크롤 위치를 0으로 강제 설정하는 함수
  const forceScrollTop = () => {
    // 메인 문서 요소들의 스크롤 초기화
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // iOS/Safari 대응
    if (window.pageYOffset !== undefined) {
      window.scrollTo(0, 0);
    }

    // 컨텐츠 래퍼 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }

    // 추가 스크롤 초기화를 위해 지연 실행
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 50);
  };

  // 다음 주소 검색 실행 함수
  const openPostcodeSearch = () => {
    // 기존 container가 있다면 제거
    const existingContainer = document.getElementById("postcode-container");
    if (existingContainer) {
      existingContainer.remove();
    }

    // 새 container 생성
    const postcodeContainer = document.createElement("div");
    postcodeContainer.id = "postcode-container";
    postcodeContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    // 내부 컨테이너 생성
    const innerContainer = document.createElement("div");
    innerContainer.style.cssText = `
      position: relative;
      width: 90%;
      max-width: 500px;
      height: 500px;
      background-color: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    // 내부 컨테이너 클릭 시 이벤트 버블링 방지
    innerContainer.onclick = function (e) {
      e.stopPropagation();
    };

    postcodeContainer.appendChild(innerContainer);
    document.body.appendChild(postcodeContainer);

    // 외부 영역 클릭 시 모달 닫기
    postcodeContainer.onclick = function () {
      document.body.removeChild(postcodeContainer);
    };

    new window.daum.Postcode({
      oncomplete: function (data) {
        // 도로명 주소 또는 지번 주소 선택
        let addr = data.roadAddress || data.jibunAddress;

        // 주소 필드 업데이트
        handleChange("address")(addr);

        // 상세주소 필드에 포커스
        setTimeout(() => {
          if (detailAddressRef.current) {
            detailAddressRef.current.focus();
          }
        }, 100);

        // 검색 완료 후 컨테이너 제거
        document.body.removeChild(postcodeContainer);
      },
      // 검색창 크기 및 UI 설정
      width: "100%",
      height: "100%",
      maxSuggestItems: 5,
      theme: {
        searchBgColor: "#FFFFFF", // 검색창 배경색을 흰색으로 변경
        queryTextColor: "#333333", // 검색창 텍스트 색상을 어두운 색으로 변경
        emphTextColor: "#0075FF", // 강조 텍스트 색상
        outlineColor: "#e0e0e0", // 테두리 색상
      },
      alwaysShowEngAddr: false,
      hideMapBtn: true,
      hideEngBtn: true,
      shorthand: true,
      animation: true,
    }).embed(innerContainer);
  };

  const handleChange = (key) => (e) => {
    let value = e;

    // 이벤트 객체인 경우 값 추출
    if (e && e.target && e.target.value !== undefined) {
      value = e.target.value;
    }

    // 이름 필드에 영어 입력 감지 및 처리
    if (key === "name" && containsEnglish(value)) {
      showForeignerToast(); // 외국인 알림 토스트 표시
      value = value.replace(/[a-zA-Z]/g, ""); // 영어 문자 제거
    }

    // 생년월일은 숫자만 입력 가능하게 처리
    if (key === "birth") {
      value = value.replace(/[^0-9]/g, "").slice(0, 8);

      // 8자리가 다 채워졌을 때 키보드 닫기
      if (value.length === 8 && birthRef.current) {
        birthRef.current.blur();
      }
    }

    // 전화번호는 숫자만 입력 가능하게 처리
    if (key === "phone") {
      value = value.replace(/[^0-9]/g, "").slice(0, 11);

      // 11자리가 다 채워졌을 때 키보드 닫기
      if (value.length === 11 && phoneRef.current) {
        phoneRef.current.blur();
      }
    }

    setForm((prevForm) => ({ ...prevForm, [key]: value }));

    // 현재 공동 소유자 데이터 업데이트
    setCoOwners((prevOwners) => {
      const updatedOwners = [...prevOwners];
      updatedOwners[currentCoOwnerIndex] = {
        ...updatedOwners[currentCoOwnerIndex],
        [key]: value,
      };
      return updatedOwners;
    });

    // 입력 중에는 에러 메시지 초기화
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // 이름 유효성 검사
  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return "이름을 입력해주세요";
    }
    if (!/^[가-힣\s]+$/.test(name)) {
      return "한글 이름만 입력 가능합니다";
    }
    return "";
  };

  // 생년월일 유효성 검사
  const validateBirth = (birth) => {
    const birthRegex = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;
    if (!birthRegex.test(birth)) {
      return "올바른 생년월일 형식이 아닙니다. (예: 19990315)";
    }

    // 미성년자 확인 (2007년 1월 1일 이후 출생자)
    if (parseInt(birth) >= 20070101) {
      return "미성년자는 신청이 불가합니다. (2007년 1월 1일 이후 출생자)";
    }

    return "";
  };

  // 전화번호 유효성 검사
  const validatePhone = (phone) => {
    const phoneRegex = /^01[0-9]{8,9}$/;
    if (!phoneRegex.test(phone)) {
      return "올바른 휴대폰 번호 형식이 아닙니다.";
    }
    return "";
  };

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email)) {
      return "올바른 이메일 주소 형식이 아닙니다.";
    }
    return "";
  };

  // 계좌번호 유효성 검사
  const validateAccountNumber = (accountNumber) => {
    const accountRegex = /^[0-9-]+$/;
    if (!accountRegex.test(accountNumber)) {
      return "계좌번호는 숫자만 입력 가능합니다.";
    }
    return "";
  };

  // 예금주명 유효성 검사
  const validateAccountHolder = (accountHolder) => {
    if (!accountHolder || accountHolder.trim().length < 2) {
      return "예금주명을 정확히 입력해주세요.";
    }
    // 공백을 모두 제거하고 비교
    const cleanAccountHolder = accountHolder.replace(/\s+/g, "");
    const cleanName = form.name.replace(/\s+/g, "");
    if (cleanAccountHolder !== cleanName) {
      return "예금주명은 입력하신 이름과 동일해야 합니다.";
    }
    return "";
  };

  // 진동 효과 실행 후 초기화
  useEffect(() => {
    // 진동 효과가 있는 필드 확인
    const hasShakingField = Object.values(shakingFields).some((val) => val);

    if (hasShakingField) {
      const timer = setTimeout(() => {
        setShakingFields({
          name: false,
          birth: false,
          phone: false,
          email: false,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakingFields]);

  // 현재 공동 소유자 정보 유효성 검사
  const validateCurrentCoOwner = () => {
    const nameError = validateName(form.name);
    const birthError = validateBirth(form.birth);
    const phoneError = validatePhone(form.phone);
    const emailError = validateEmail(form.email);
    let isValid = true;

    setErrors({
      name: nameError,
      birth: birthError,
      phone: phoneError,
      email: emailError,
    });

    if (nameError) {
      if (nameRef.current) nameRef.current.focus();
      setShakingFields({ ...shakingFields, name: true });
      isValid = false;
    } else if (birthError) {
      if (birthRef.current) birthRef.current.focus();
      setShakingFields({ ...shakingFields, birth: true });
      isValid = false;
    } else if (phoneError) {
      if (phoneRef.current) phoneRef.current.focus();
      setShakingFields({ ...shakingFields, phone: true });
      isValid = false;
    } else if (emailError) {
      if (emailRef.current) emailRef.current.focus();
      setShakingFields({ ...shakingFields, email: true });
      isValid = false;
    }

    return isValid;
  };

  // 현재 공동 소유자 입력 완료 여부 확인
  const isCurrentCoOwnerFilled = () => {
    return (
      form.name?.trim() !== "" &&
      form.birth?.trim() !== "" &&
      form.phone?.trim() !== "" &&
      form.email?.trim() !== "" &&
      form.address?.trim() !== "" &&
      form.bank !== null &&
      form.accountNumber?.trim() !== "" &&
      form.accountHolder?.trim() !== ""
    );
  };

  // 공동 소유자 완료 처리
  const handleCoOwnerComplete = () => {
    const isValid = validateCurrentCoOwner();
    if (isValid) {
      // 계좌 정보 유효성 검사
      const bankError = !form.bank ? "은행을 선택해주세요." : "";
      const accountNumberError = validateAccountNumber(form.accountNumber);
      const accountHolderError = validateAccountHolder(form.accountHolder);

      // 계좌 정보 관련 에러 설정
      setErrors((prev) => ({
        ...prev,
        bank: bankError,
        accountNumber: accountNumberError,
        accountHolder: accountHolderError,
      }));

      // 계좌 정보 유효성 검사 실패 시 처리
      if (bankError) {
        if (bankSelectRef.current) bankSelectRef.current.focus();
        return;
      } else if (accountNumberError) {
        if (accountNumberRef.current) accountNumberRef.current.focus();
        return;
      } else if (accountHolderError) {
        if (accountHolderRef.current) accountHolderRef.current.focus();
        return;
      }

      const updatedCoOwners = [...coOwners];
      updatedCoOwners[currentCoOwnerIndex] = { ...form };

      // 쿠키에서 대표 신청자 전화번호 가져오기
      const verifiedPhoneNumber = Cookies.get("verifiedPhoneNumber");

      // 현재 입력한 전화번호가 대표 신청자 번호와 일치하는지 확인
      if (verifiedPhoneNumber && form.phone === verifiedPhoneNumber) {
        // 일치하면 모달 표시
        setIsRepresentativePhone(true);
        setShowPhoneModal(true);
        return; // 함수 종료하여 나머지 로직은 모달 응답 후 처리
      }

      // 나머지 코드 (전화번호가 일치하지 않는 경우)
      setCoOwners(updatedCoOwners);

      // 해당 인덱스를 완료로 표시
      const newCompleted = [...isCompleted];
      newCompleted[currentCoOwnerIndex] = true;
      setIsCompleted(newCompleted);

      // 편집 모드 종료
      const newEditMode = [...editMode];
      newEditMode[currentCoOwnerIndex] = false;
      setEditMode(newEditMode);
    }
  };

  // 수정 모드로 전환하는 함수 추가
  const handleEditMode = () => {
    // 편집 모드 활성화
    const newEditMode = [...editMode];
    newEditMode[currentCoOwnerIndex] = true;
    setEditMode(newEditMode);
  };

  // 대표 신청자 전화번호 모달 확인 처리
  const handlePhoneModalConfirm = () => {
    setShowPhoneModal(false);

    // 현재 폼 초기화
    setForm({
      ...form,
      phone: "", // 전화번호만 초기화
    });

    // coOwners 배열도 업데이트
    const updatedCoOwners = [...coOwners];
    updatedCoOwners[currentCoOwnerIndex] = {
      ...updatedCoOwners[currentCoOwnerIndex],
      phone: "",
    };
    setCoOwners(updatedCoOwners);

    // 전화번호 필드에 포커스
    if (phoneRef.current) {
      setTimeout(() => {
        phoneRef.current.focus();
      }, 100);
    }
  };

  // 대표 신청자 전화번호 모달 취소 처리
  const handlePhoneModalCancel = () => {
    setShowPhoneModal(false);

    // 일반적인 완료 처리 진행
    const updatedCoOwners = [...coOwners];
    updatedCoOwners[currentCoOwnerIndex] = { ...form };
    setCoOwners(updatedCoOwners);

    // 해당 인덱스를 완료로 표시
    const newCompleted = [...isCompleted];
    newCompleted[currentCoOwnerIndex] = true;
    setIsCompleted(newCompleted);

    // 편집 모드 종료
    const newEditMode = [...editMode];
    newEditMode[currentCoOwnerIndex] = false;
    setEditMode(newEditMode);
  };

  // 공동 소유자 추가 (애니메이션 강화)
  const handleAddCoOwner = () => {
    // 최대 10명까지만 추가 가능
    if (coOwners.length < 10) {
      const newCoOwnerId = coOwners.length + 1;

      // 새 공동 소유자 객체 생성
      const newCoOwner = {
        id: newCoOwnerId,
        name: "",
        birth: "",
        phone: "",
        email: "",
        address: "",
        detailAddress: "",
        bank: null,
        accountNumber: "",
        accountHolder: "",
      };

      // 애니메이션을 위한 딜레이 추가
      setTimeout(() => {
        setCoOwners((prev) => [...prev, newCoOwner]);

        // 새 소유자 추가 시 편집 모드로 설정
        const newEditMode = [...editMode];
        newEditMode[coOwners.length] = true;
        setEditMode(newEditMode);

        // 새로 추가된 소유자로 이동
        setCurrentCoOwnerIndex(coOwners.length);

        // 스크롤 맨 위로
        forceScrollTop();
      }, 50);

      // 자동으로 새 항목으로 스크롤
      setTimeout(() => {
        const scrollContainer = document.querySelector(
          ".co-owner-scroll-container"
        );
        if (scrollContainer) {
          scrollContainer.scrollTo({
            left: scrollContainer.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 200);
    }
  };

  // 이전 소유자와 동일한 주소 사용
  const [useSameAddress, setUseSameAddress] = useState(false);

  // 이전 소유자의 주소를 가져오는 함수
  const getPreviousOwnerAddress = () => {
    if (representativeAddress) {
      // 대표 신청자 주소가 있으면 그것을 반환
      return representativeAddress;
    } else if (currentCoOwnerIndex > 0) {
      // 첫 번째 이전 소유자를 찾음 (완료된 소유자여야 함)
      for (let i = currentCoOwnerIndex - 1; i >= 0; i--) {
        if (isCompleted[i]) {
          return {
            address: coOwners[i].address,
            detailAddress: coOwners[i].detailAddress,
          };
        }
      }
    }
    return { address: "", detailAddress: "" };
  };

  // 주소 동일 체크박스 핸들러
  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setUseSameAddress(checked);

    // 체크박스 상태 저장
    const newSameAddressStates = [...sameAddressStates];
    newSameAddressStates[currentCoOwnerIndex] = checked;
    setSameAddressStates(newSameAddressStates);

    if (checked) {
      // 체크박스 선택 시 이전 소유자의 주소 사용
      const { address, detailAddress } = getPreviousOwnerAddress();
      handleChange("address")(address);
      handleChange("detailAddress")(detailAddress);
    } else {
      // 체크박스 해제 시 주소 정보 비우기
      handleChange("address")("");
      handleChange("detailAddress")("");
    }
  };

  // 공동 소유자 삭제
  const handleDeleteCoOwner = (indexToDelete) => {
    if (coOwners.length > 1) {
      // 삭제 전 coOwners 배열의 길이 저장
      const prevLength = coOwners.length;

      // 새로운 인덱스 계산
      let newIndex = indexToDelete;

      // 삭제하려는 항목이 마지막 항목이면 이전 항목으로 이동
      if (indexToDelete === prevLength - 1) {
        newIndex = indexToDelete - 1;
      }
      // 삭제하려는 항목이 첫 번째 항목이 아니면서 현재 선택된 항목이면 이전 항목으로 이동
      else if (indexToDelete === currentCoOwnerIndex && indexToDelete > 0) {
        newIndex = indexToDelete - 1;
      }
      // 삭제하려는 항목이 현재 선택된 항목보다 앞에 있으면 인덱스 조정
      else if (indexToDelete < currentCoOwnerIndex) {
        newIndex = currentCoOwnerIndex - 1;
      }

      // 먼저 배열 업데이트
      const updatedCoOwners = coOwners.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedCompleted = isCompleted.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedEditMode = editMode.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedSameAddressStates = sameAddressStates.filter(
        (_, index) => index !== indexToDelete
      );

      // 상태 업데이트
      setCoOwners(updatedCoOwners);
      setIsCompleted(updatedCompleted);
      setEditMode(updatedEditMode);
      setSameAddressStates(updatedSameAddressStates);

      // 인덱스 업데이트 (다음 렌더링 사이클에서 실행)
      setTimeout(() => {
        setCurrentCoOwnerIndex(newIndex);
      }, 0);

      // 삭제 후 스크롤 조정
      setTimeout(() => {
        const scrollContainer = document.querySelector(
          ".co-owner-scroll-container"
        );
        const currentItem = document.querySelector(
          `.co-owner-item-${newIndex}`
        );

        if (scrollContainer && currentItem) {
          scrollContainer.scrollTo({
            left: currentItem.offsetLeft - 40,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  // 공동 소유자 선택
  const handleSelectCoOwner = (index) => {
    setCurrentCoOwnerIndex(index);
    setForm(coOwners[index]);

    // 선택된 항목이 스크롤 컨테이너에서 보이도록 스크롤
    setTimeout(() => {
      const scrollContainer = document.querySelector(
        ".co-owner-scroll-container"
      );
      const selectedItem = document.querySelector(`.co-owner-item-${index}`);

      if (scrollContainer && selectedItem) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();

        // 아이템이 컨테이너 밖에 있는 경우 스크롤
        if (
          itemRect.left < containerRect.left ||
          itemRect.right > containerRect.right
        ) {
          scrollContainer.scrollTo({
            left:
              selectedItem.offsetLeft -
              containerRect.width / 2 +
              itemRect.width / 2,
            behavior: "smooth",
          });
        }
      }
    }, 100);
  };

  // 최종 제출 처리
  const handleSubmit = async (e) => {
    // 중요: 이벤트 기본 동작 방지
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // 완성된 공동 소유자 데이터만 필터링
    const completedOwners = coOwners.filter((_, index) => isCompleted[index]);

    // 최종 유효성 검사 수행
    let hasErrors = false;
    completedOwners.forEach((owner) => {
      // 이름 검사
      const nameError = validateName(owner.name);
      if (nameError) {
        setErrors((prev) => ({ ...prev, name: nameError }));
        hasErrors = true;
      }

      // 생년월일 검사
      const birthError = validateBirth(owner.birth);
      if (birthError) {
        setErrors((prev) => ({ ...prev, birth: birthError }));
        hasErrors = true;
      }

      // 전화번호 검사
      const phoneError = validatePhone(owner.phone);
      if (phoneError) {
        setErrors((prev) => ({ ...prev, phone: phoneError }));
        hasErrors = true;
      }

      // 이메일 검사
      const emailError = validateEmail(owner.email);
      if (emailError) {
        setErrors((prev) => ({ ...prev, email: emailError }));
        hasErrors = true;
      }

      // 계좌번호 검사
      const accountNumberError = validateAccountNumber(owner.accountNumber);
      if (accountNumberError) {
        setErrors((prev) => ({ ...prev, accountNumber: accountNumberError }));
        hasErrors = true;
      }

      // 예금주명 검사 - 공백을 모두 제거하고 비교
      const cleanAccountHolder = owner.accountHolder.replace(/\s+/g, "");
      const cleanName = owner.name.replace(/\s+/g, "");
      if (cleanAccountHolder !== cleanName) {
        setErrors((prev) => ({
          ...prev,
          accountHolder: "예금주명은 입력하신 이름과 동일해야 합니다.",
        }));
        hasErrors = true;
      }

      // 은행 선택 검사
      if (!owner.bank) {
        setErrors((prev) => ({ ...prev, bank: "은행을 선택해주세요." }));
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setToastMessage("입력하신 정보를 다시 확인해주세요.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return;
    }

    // 예금주명과 이름이 일치하는지 최종 확인
    const hasAccountHolderMismatch = completedOwners.some(
      (owner) => owner.accountHolder.trim() !== owner.name.trim()
    );

    if (hasAccountHolderMismatch) {
      setToastMessage("예금주명은 입력하신 이름과 동일해야 합니다.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return;
    }

    setIsLoading(true);

    try {
      // 쿠키에 공동 소유자 정보 저장 (빈 값 제외)
      Cookies.set("coOwners", JSON.stringify(completedOwners), { expires: 7 });

      // 다음 페이지로 이동
      navigateTo(redirectPath || "/news-proof");
      return true;
    } catch (error) {
      console.error("오류 발생:", error);
      setToastMessage("데이터 저장 중 오류가 발생했습니다");
      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 3000);

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // 페이지 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
      },
    },
  };

  // 공동 소유자 추가 버튼 활성화 조건
  const canAddNewCoOwner = () => {
    // 모든 공동 소유자가 완료되었는지 확인
    const allCompleted = coOwners.every(
      (owner, index) =>
        !owner.name || owner.name.trim() === "" || isCompleted[index]
    );

    // 10명 제한 확인
    const underLimit = coOwners.length < 10;

    // 적어도 한 명의 완료된 공동 소유자가 있는지 확인
    const hasCompletedOwner = isCompleted.some((completed) => completed);

    return allCompleted && underLimit && hasCompletedOwner;
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 두 번째 토스트 애니메이션 (외국인 접수 불가 알림)
  const toast2Variants = {
    initial: {
      opacity: 0,
      y: -80,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: -80,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 데이터 컬렉션 초기 설정
  useEffect(() => {
    // 다른 페이지로부터 리디렉션된 경우
    const { state } = location;
    if (state?.redirectPath) {
      setRedirectPath(state.redirectPath);
    }

    // 쿠키에서 대표 신청자 전화번호 가져오기
    const verifiedPhoneNumber = Cookies.get("verifiedPhoneNumber");
    if (verifiedPhoneNumber) {
    }

    // 이전 페이지에서 설정한 쿠키에서 데이터 가져오기
    const savedCoOwners = Cookies.get("coOwners");
    if (savedCoOwners) {
      try {
        const coOwnersData = JSON.parse(savedCoOwners);
        setCoOwners(coOwnersData);
      } catch (error) {
        console.error("오류 발생:", error);
      }
    }
  }, [location]);

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>3. 공동신청자 정보 입력</Title>
          </div>
          <Header>
            <StepIndicator>3 / 4</StepIndicator>
            <SubTitle>공동신청자 정보입력</SubTitle>
            <Description>신청 인원 수만큼 반복하여 입력해주세요.</Description>
            <InfoMessage>
              <InfoIcon>ⓘ</InfoIcon>
              <InfoTextContainer>
                <>
                  <InfoTextBold>
                    본 신청은{" "}
                    <HighlightedText>미성년자와 외국인</HighlightedText>은
                    접수받지않습니다.
                  </InfoTextBold>
                  <InfoTextRegular>
                    대표신청자는 본 입력 양식을 작성하지 않으셔도 됩니다.
                  </InfoTextRegular>
                </>
              </InfoTextContainer>
            </InfoMessage>
          </Header>

          {/* 공동 소유자 목록 */}
          <CoOwnerListContainer>
            <CoOwnerScrollContainer className="co-owner-scroll-container">
              <AnimatePresence mode="popLayout">
                {coOwners.map((owner, index) => (
                  <CoOwnerItem
                    key={`owner-${index}`}
                    className={`co-owner-item-${index}`}
                    isActive={index === currentCoOwnerIndex}
                    isCompleted={isCompleted[index]}
                    isEditing={editMode[index]}
                    onClick={() => handleSelectCoOwner(index)}
                    as={motion.div}
                    variants={listItemVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    layoutId={`owner-item-${index}`}
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{
                      scale: 0.95,
                      transition: { duration: 0.1 },
                    }}
                  >
                    {coOwners.length > 1 && (
                      <ItemDeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCoOwner(index);
                        }}
                      >
                        ×
                      </ItemDeleteButton>
                    )}
                    <CoOwnerName isActive={index === currentCoOwnerIndex}>
                      <NameText>{owner.name || `신청자 ${index + 1}`}</NameText>
                      {index === currentCoOwnerIndex && !isCompleted[index] && (
                        <StatusText>입력중</StatusText>
                      )}
                      {isCompleted[index] && (
                        <CheckIcon
                          src="/png/CheckSuc.png"
                          alt="완료"
                          as={motion.img}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{
                            scale: 1,
                            opacity: 1,
                            transition: { delay: 0.2, duration: 0.3 },
                          }}
                        />
                      )}
                    </CoOwnerName>
                  </CoOwnerItem>
                ))}
              </AnimatePresence>
            </CoOwnerScrollContainer>
          </CoOwnerListContainer>

          <FormContainer
            as={motion.div}
            variants={formContainerAnimation}
            initial="initial"
            animate="animate"
            exit="exit"
            key={`form-container-${currentCoOwnerIndex}`}
          >
            {apiError && <ErrorAlert>{apiError}</ErrorAlert>}
            <SectionTitle>신원정보를 입력해주세요</SectionTitle>
            <FormSection
              as={motion.div}
              variants={formContentVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <FormGroup>
                <Label>
                  이름<Required>*</Required>
                </Label>
                <Input
                  type="text"
                  placeholder="이름을 입력하세요"
                  value={form.name}
                  onChange={(e) => {
                    // 입력 중에는 모든 값을 허용
                    handleChange("name")(e);
                  }}
                  onBlur={(e) => {
                    // 포커스를 잃었을 때 한글만 남기고 필터링
                    const filteredName = e.target.value
                      .replace(/[^가-힣\s]/g, "")
                      .trim();
                    handleChange("name")(filteredName);
                  }}
                  ref={nameRef}
                  hasError={!!errors.name}
                  isShaking={shakingFields.name}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  생년월일<Required>*</Required>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="예시: 19990315"
                  value={form.birth}
                  onChange={(e) => handleChange("birth")(e)}
                  ref={birthRef}
                  hasError={!!errors.birth}
                  isShaking={shakingFields.birth}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.birth && <ErrorMessage>{errors.birth}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  신청인 연락처<Required>*</Required>
                </Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="- 없이 숫자만 입력"
                  value={form.phone}
                  onChange={(e) => handleChange("phone")(e)}
                  ref={phoneRef}
                  hasError={!!errors.phone}
                  isShaking={shakingFields.phone}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  주소<Required>*</Required>
                  {(currentCoOwnerIndex > 0 || representativeAddress) && (
                    <SameAddressCheckbox>
                      <input
                        type="checkbox"
                        checked={useSameAddress}
                        onChange={handleSameAddressChange}
                        id="sameAddressCheckbox"
                        disabled={!editMode[currentCoOwnerIndex]}
                      />
                      <label htmlFor="sameAddressCheckbox">
                        {currentCoOwnerIndex === 0 && representativeAddress
                          ? "대표 신청자와 동일"
                          : "대표 신청자와 동일"}
                      </label>
                    </SameAddressCheckbox>
                  )}
                </Label>
                <AddressInputContainer>
                  <AddressInput
                    type="text"
                    placeholder="주소 검색"
                    value={form.address}
                    readOnly
                    ref={addressRef}
                    onClick={
                      editMode[currentCoOwnerIndex]
                        ? openPostcodeSearch
                        : undefined
                    }
                    disabled={!editMode[currentCoOwnerIndex]}
                  />
                </AddressInputContainer>
              </FormGroup>

              <FormGroup>
                <Label>상세주소</Label>
                <Input
                  type="text"
                  placeholder="상세 주소를 입력해주세요"
                  value={form.detailAddress}
                  onChange={(e) => handleChange("detailAddress")(e)}
                  ref={detailAddressRef}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  이메일 주소<Required>*</Required>
                </Label>
                <Input
                  type="email"
                  placeholder="예시: example@gmail.com"
                  value={form.email}
                  onChange={(e) => handleChange("email")(e)}
                  ref={emailRef}
                  hasError={!!errors.email}
                  isShaking={shakingFields.email}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  은행선택<Required>*</Required>
                </Label>
                <BankSelect
                  ref={bankSelectRef}
                  value={form.bank}
                  options={bankOptions}
                  onChange={(selectedOption) => {
                    handleChange("bank")(selectedOption);
                  }}
                  placeholder="은행을 선택해주세요"
                  classNamePrefix="select"
                  components={{
                    Option: CustomOption,
                    SingleValue: CustomSingleValue,
                  }}
                  isSearchable={true}
                  noOptionsMessage={() => "검색 결과가 없습니다"}
                  loadingMessage={() => "검색 중..."}
                  isDisabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.bank && <ErrorText>{errors.bank}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label>
                  계좌번호<Required>*</Required>
                </Label>
                <Input
                  ref={accountNumberRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.accountNumber}
                  onChange={(e) => handleChange("accountNumber")(e)}
                  placeholder="계좌번호를 입력해주세요"
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.accountNumber && (
                  <ErrorText>{errors.accountNumber}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  예금주명<Required>*</Required>
                </Label>
                <Input
                  ref={accountHolderRef}
                  type="text"
                  value={form.accountHolder}
                  onChange={(e) => handleChange("accountHolder")(e)}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const cleanAccountHolder = value.replace(/\s+/g, "");
                      const cleanName = form.name.replace(/\s+/g, "");
                      if (cleanAccountHolder !== cleanName) {
                        setErrors((prev) => ({
                          ...prev,
                          accountHolder:
                            "예금주명은 입력하신 이름과 동일해야 합니다.",
                        }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          accountHolder: "",
                        }));
                      }
                    }
                  }}
                  placeholder="예금주명을 입력해주세요"
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.accountHolder && (
                  <ErrorText>{errors.accountHolder}</ErrorText>
                )}
              </FormGroup>

              {/* 버튼 섹션을 폼 내부로 이동 */}
              <ActionButtonsContainer>
                <DeleteButton
                  onClick={() => handleDeleteCoOwner(currentCoOwnerIndex)}
                  disabled={coOwners.length <= 1}
                >
                  <TrashIcon src="/png/trash.png" alt="삭제" />
                  삭제
                </DeleteButton>
                {editMode[currentCoOwnerIndex] ? (
                  <CompleteButton
                    onClick={handleCoOwnerComplete}
                    disabled={!isCurrentCoOwnerFilled()}
                  >
                    작성 완료
                  </CompleteButton>
                ) : (
                  <EditButton
                    onClick={handleEditMode}
                    disabled={!isCompleted[currentCoOwnerIndex]}
                  >
                    수정하기
                  </EditButton>
                )}
              </ActionButtonsContainer>
            </FormSection>
          </FormContainer>

          <BottomButtonsContainer>
            <AddCoOwnerButton
              onClick={handleAddCoOwner}
              disabled={!canAddNewCoOwner()}
            >
              <PlusIcon src="/png/plus.png" alt="추가" /> 신청자 추가
            </AddCoOwnerButton>

            <ActionButton
              onClick={(e) => handleSubmit(e)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                isLoading || !isCompleted.some((completed) => completed)
              }
            >
              {isLoading ? "처리 중..." : "다음으로"}
            </ActionButton>
          </BottomButtonsContainer>

          {/* 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <AlertIcon>!</AlertIcon>빈 공동 소유자 폼이 있습니다.
                <br /> 삭제하거나 완료해주세요
              </Toast>
            )}
          </AnimatePresence>

          {/* 외국인 접수 불가 알림 */}
          <AnimatePresence>
            {showToast2 && (
              <Toast2
                variants={toast2Variants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <AlertIcon>!</AlertIcon>
                {toastMessage2}
              </Toast2>
            )}
          </AnimatePresence>

          {/* 대표 신청자 전화번호 모달 */}
          <AnimatePresence>
            {showPhoneModal && (
              <ModalOverlay>
                <ModalContainer
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <ModalTitle>대표 신청자 번호 사용 불가</ModalTitle>
                  <ModalContent>
                    입력하신 전화번호는 대표 신청자의 번호입니다. 대표 신청자의
                    번호는 공동 신청자로 사용할 수 없습니다. 다른 전화번호를
                    입력해 주세요.
                  </ModalContent>
                  <ModalButtonGroup>
                    <ModalConfirmButton
                      onClick={handlePhoneModalConfirm}
                      style={{ width: "100%" }}
                    >
                      확인
                    </ModalConfirmButton>
                  </ModalButtonGroup>
                </ModalContainer>
              </ModalOverlay>
            )}
          </AnimatePresence>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 공동 소유자 목록 스타일
const CoOwnerListContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  padding: 5px 0;
`;

const CoOwnerScrollContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  height: 60px;
  margin: 0 auto;
  overflow-x: auto;
  padding: 5px 0;
  -webkit-overflow-scrolling: touch; /* iOS 스크롤 스무스하게 */
  scroll-behavior: smooth;

  /* 스크롤바 숨김 처리 */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const CoOwnerItem = styled(motion.div)`
  position: relative;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid
    ${(props) => {
      if (props.isCompleted && !props.isEditing) return "#4CAF50";
      if (props.isActive) return "#0075FF";
      return "#E0E0E0";
    }};
  background-color: ${(props) => {
    if (props.isCompleted && !props.isEditing) return "rgba(76, 175, 80, 0.1)";
    if (props.isActive && !props.isCompleted) return "rgba(0, 117, 255, 0.1)";
    if (props.isCompleted && props.isEditing) return "rgba(255, 193, 7, 0.1)";
    return "white";
  }};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  width: 120px;
  flex: 0 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  will-change: transform, opacity;

  @media (max-width: 480px) {
    width: 120px;
    min-width: 70px;
  }
`;

const ItemDeleteButton = styled.button`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ff4d4f;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  opacity: 1;
  z-index: 2;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const CoOwnerName = styled.div`
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.isActive ? "flex-start" : "center")};
  gap: 6px;
  width: 100%;
  text-align: ${(props) => (props.isActive ? "left" : "center")};
  overflow: hidden;
  padding: 0 2px;
  white-space: nowrap;
  font-size: 14px;
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const StatusText = styled.span`
  color: #0075ff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const InputStatusText = styled.span`
  font-size: 11px;
  color: #0075ff;
  margin-top: 4px;
`;

const CheckIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

// 버튼 컨테이너 스타일
const ActionButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  color: #dc4b3f;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    text-decoration: underline;
  }
`;

const TrashIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-right: 6px;
`;

const CompleteButton = styled.button`
  display: flex;
  width: 90px;
  height: 32px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${(props) => (props.disabled ? "#CBCFD5" : "#4CAF50")};
  border: none;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  transition: background-color 0.2s;

  &:hover {
    background: ${(props) => (props.disabled ? "#CBCFD5" : "#419544")};
  }
`;

const EditButton = styled.button`
  display: flex;
  width: 90px;
  height: 32px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0075ff;
  border: none;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: #0075ff;
  }
`;

const BottomButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
`;

const AddCoOwnerButton = styled.div`
  display: flex;
  width: 156px;
  height: 48px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  color: #0075ff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
  }
`;

const PlusIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

// 추가 스타일
const AddressInputContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const AddressInput = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "white")};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #0075ff;
    outline: none;
  }
`;

// 애니메이션 정의
const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  align-self: flex-start;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 5px;
  font-size: 16px;
  margin-right: 10px;
  font-weight: bold;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;
const InfoTextRegular = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 50px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const FormContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  border-radius: 12px;
  border: 1px solid rgba(0, 117, 255, 0.3);
  overflow: hidden;
  background-color: white;
  will-change: height;
`;

const FormSection = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  padding: 15px 20px;
  margin: 0;
  border-radius: 12px 12px 0px 0px;
  background: #eef4fe;
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2e3840;
`;

const Required = styled.span`
  color: #fe7156;
  margin-left: 2px;
`;

const Input = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? "#FF4D4F" : "#e5e5e5")};
  padding: 0 16px;
  font-size: 16px;
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "white")};
  width: 100%;
  animation: ${(props) => (props.isShaking ? shakeAnimation : "none")} 0.5s;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: ${(props) => (props.hasError ? "#FF4D4F" : "#0075FF")};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 91px;
  height: 48px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  transition: background-color 0.2s;

  &:hover {
    background: #0066df;
  }
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const NavigationButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const SameAddressCheckbox = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  font-size: 12px;
  color: #666;

  input {
    margin-right: 5px;
  }

  label {
    cursor: pointer;
    font-weight: normal;
  }
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 38.5%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const AlertIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #0075ff;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

const Toast2 = styled(motion.div)`
  position: fixed;
  top: 230px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 32%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const AlertIcon2 = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: white;
  color: #0075ff;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 12px;
  font-weight: bold;
`;

const BankSelect = styled(Select)`
  .select__control {
    height: 50px;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: none;
    background: white;

    &:hover {
      border-color: #0075ff;
    }

    &--is-focused {
      border-color: #0075ff;
      box-shadow: 0 0 0 1px #0075ff;
    }
  }

  .select__placeholder {
    color: #999;
  }

  .select__single-value {
    color: #333;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .select__input {
    color: #333 !important;

    input {
      color: #333 !important;
    }
  }

  .select__input-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #333;

    input {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      border: none !important;
      position: absolute;
      color: #333 !important;

      &:focus {
        opacity: 1 !important;
        width: 100% !important;
        height: auto !important;
        position: relative;
      }
    }
  }

  .select__value-container {
    padding: 2px 16px;
    cursor: pointer;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .select__menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 8px;
    z-index: 100;
    position: absolute;
    width: 100%;
    background: white;
  }

  .select__menu-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 8px 0;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }
  }

  .select__indicator-separator {
    display: none;
  }

  .select__clear-indicator {
    cursor: pointer;
    color: #999;

    &:hover {
      color: #666;
    }
  }

  .select__dropdown-indicator {
    color: #999;
    padding: 0 16px;

    &:hover {
      color: #666;
    }
  }
`;

const OptionContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BankIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-top: 4px;
`;

export default CoOwnerDataCollection;
